/**
 * PTT bubble: brand and distance of the next reachable station along the route.
 * Time, side-of-road and frontage detail live in the confirm sheet - the pill
 * itself stays a two-word glance.
 *
 * Only ever shows a station that survived the corridor / ahead / side-of-road
 * filters in nav/fuelStops.ts, so tapping it can never send the driver across
 * oncoming traffic or into a U-turn.
 *
 * Tapping it opens a confirmation showing which station, how far, and which
 * side of the road it is on. The driver asked for the confirm step rather than
 * an immediate re-route: a stray touch on a dashboard mount should not silently
 * rewrite the route of a 400 km run.
 *
 * The confirmation offers TWO stations, not one, because of frontage roads. On
 * a divided highway the nearest PTT is often served by a parallel service road,
 * so committing to it means leaving the carriageway well before you can see the
 * forecourt. Being shown the one after it - usually a straightforward pull-in -
 * lets the driver make that call at a glance instead of discovering it at 90.
 */

import type { FuelStop } from '../nav/fuelStops';
import { formatDistance, formatDuration } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

export interface FuelChoice {
  stop: FuelStop;
  /** Metres from here to the station. */
  remaining: number;
  /** Seconds. */
  eta: number;
}

export class FuelBubble {
  readonly el: HTMLElement;
  readonly container: HTMLElement;
  private otherEl: HTMLButtonElement;
  private confirmEl: HTMLElement;
  private choices: FuelChoice[] = [];
  /** Every reachable station ahead, for the chooser. */
  private all: FuelChoice[] = [];
  /** What the sheet is currently offering, in display order. */
  private picks: FuelChoice[] = [];
  private lastUnits: UnitSystem = 'metric';
  /** What the pill currently reads, so an unchanged pill is left alone. */
  private lastKey = '';

  constructor(
    parent: HTMLElement,
    private onDetour: (stop: FuelStop) => void,
  ) {
    this.el = document.createElement('button');
    this.el.className = 'fuel-bubble';
    this.el.hidden = true;
    this.container = document.createElement('div');
    this.container.className = 'fuel-group';
    this.container.hidden = true;
    this.otherEl = document.createElement('button');
    this.otherEl.className = 'fuel-other';
    this.otherEl.textContent = 'Fuel stations';
    this.otherEl.addEventListener('click', () => this.openConfirm(true));
    this.container.append(this.el, this.otherEl);
    parent.appendChild(this.container);
    this.el.addEventListener('click', () => this.openConfirm());

    this.confirmEl = document.createElement('div');
    this.confirmEl.className = 'fuel-confirm';
    this.confirmEl.hidden = true;
    this.confirmEl.setAttribute('role', 'dialog');
    this.confirmEl.setAttribute('aria-modal', 'true');
    this.confirmEl.setAttribute('aria-label', 'Fuel stations ahead');
    this.confirmEl.addEventListener('click', (e) => {
      if (e.target === this.confirmEl) this.closeConfirm();
    });
    parent.appendChild(this.confirmEl);
  }

  hide(): void {
    this.container.hidden = true;
    this.el.hidden = true;
    this.confirmEl.hidden = true;
    this.choices = [];
    this.all = [];
    // Force a redraw next time it appears, rather than trusting stale markup.
    this.lastKey = '';
  }

  closeConfirm(): void {
    this.confirmEl.hidden = true;
  }

  private openConfirm(other = false): void {
    // The separate chooser includes other brands; the permanent pill stays PTT + 7-Eleven.
    const seen = new Set<string>();
    const list = (other ? this.all : this.choices)
      .filter((c) => (seen.has(c.stop.id) ? false : seen.add(c.stop.id)))
      .sort((a, b) => a.remaining - b.remaining).slice(0, other ? 5 : 2);
    this.picks = list;
    const cards = list.map((c, i) => this.card(c, i)).join('');

    this.confirmEl.hidden = false;
    this.confirmEl.innerHTML = `
      <div class="fuel-confirm__card">
        <div class="fuel-confirm__head">
          <span class="fuel-confirm__q">${
            other ? 'Fuel stations ahead' : this.choices.length > 1 ? t('fuel.choose') : t('fuel.confirm')
          }</span>
          <button class="icon-btn fuel-confirm__x" data-act="cancel" aria-label="${t('fuel.close')}">
            <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor"
              stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="fuel-confirm__list">${cards || '<p class="panel__hint">No suitable stations found ahead yet.</p>'}</div>
        <div class="fuel-confirm__actions">
          <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
        </div>
      </div>`;

    this.confirmEl
      .querySelectorAll('[data-act=cancel]')
      .forEach((el) => el.addEventListener('click', () => this.closeConfirm()));
    this.confirmEl.querySelectorAll<HTMLElement>('[data-pick]').forEach((el) => {
      el.addEventListener('click', () => {
        const pick = this.picks[Number(el.dataset.pick)];
        if (!pick) return;
        this.closeConfirm();
        this.onDetour(pick.stop);
      });
    });
  }

  private card(c: FuelChoice, i: number): string {
    const frontage = c.stop.access === 'frontage';
    const slug = brandSlug(c.stop.brand);
    // The name often just repeats the brand ("PTT"); only show it when it adds
    // something, so the card is a brand and a distance rather than a wall.
    const detail = c.stop.name.trim().toLowerCase() === c.stop.brand.trim().toLowerCase()
      ? ''
      : c.stop.name;
    return `
      <button class="fuel-opt fuel-opt--${slug} ${frontage ? 'is-frontage' : ''}"
              data-pick="${i}">
        <span class="fuel-opt__mark">${escapeHtml(initials(c.stop.brand))}</span>
        <span class="fuel-opt__body">
          <span class="fuel-opt__brand">${escapeHtml(c.stop.brand)}</span>
          ${detail ? `<span class="fuel-opt__name">${escapeHtml(detail)}</span>` : ''}
          <span class="fuel-opt__meta">
            <span class="fuel-opt__dist">${formatDistance(c.remaining, this.lastUnits)}</span>
            <span>${formatDuration(c.eta)}</span>
            <span>${t(c.stop.side === 'left' ? 'fuel.left' : 'fuel.right')}</span>
          </span>
          ${
            frontage
              ? `<span class="fuel-opt__flag">${t('fuel.frontage')} - ${t(
                  'fuel.frontageNote',
                )}</span>`
              : ''
          }
        </span>
      </button>`;
  }

  /** Nearest first; the bubble shows the first, the sheet offers the first two. */
  update(
    choices: FuelChoice[],
    _nearest: FuelChoice | null,
    all: FuelChoice[],
    units: UnitSystem,
  ): void {
    this.container.hidden = false;
    this.el.hidden = false;
    this.choices = choices.filter(c => c.remaining > 0);
    this.all = all.filter(c => c.remaining > 0);
    this.lastUnits = units;
    const pillStops = this.choices.length ? this.choices : this.all;
    const head = pillStops[0];
    if (!head) {
      const html = '<span class="fuel-bubble__text"><strong>—</strong><em>Fuel</em></span>';
      if (this.lastKey !== html) { this.el.innerHTML = html; this.lastKey = html; }
      this.el.setAttribute('aria-label', 'No fuel station found ahead yet');
      return;
    }
    this.lastUnits = units;
    this.el.hidden = false;

    /*
     * Only redraw when the pill would read differently.
     *
     * It is refreshed on every GPS fix, and the distance it shows is rounded to
     * something like "12 km" - so for most of a minute the rebuilt markup was
     * identical to the markup already there. Since the pill is backdrop-blurred,
     * each needless rebuild made the GPU re-composite the blur over a map that
     * is itself moving. Comparing the rendered string is far cheaper than the
     * paint it avoids.
     */
    const html = `
      <span class="fuel-bubble__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M4 21 V5 a2 2 0 0 1 2-2 h6 a2 2 0 0 1 2 2 v16 M3 21 h12
          M6 8 h6 M16 9 l3 3 v6 a1.6 1.6 0 0 0 3 0 V10 l-2.4-2.6" fill="none" stroke="currentColor"
          stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
      <span class="fuel-bubble__text">
        <strong>${formatDistance(head.remaining, units)}</strong>
        <em>${head.stop.hasSevenEleven ? "PTT + 7-Eleven" : (head.stop.brand === "PTT" ? "PTT" : head.stop.brand)}</em>
      </span>`;

    if (html !== this.lastKey) {
      this.lastKey = html;
      this.el.innerHTML = html;
      this.el.setAttribute(
        'aria-label',
        `${head.stop.name}, ${formatDistance(head.remaining, units)}`,
      );
    }
  }
}

/**
 * Brand key for colouring. The card is recognised by its colour before any of
 * the text is read - which is the point of it at 90 km/h with one hand.
 */
function brandSlug(brand: string): string {
  const b = brand.toLowerCase();
  for (const k of ['ptt', 'bangchak', 'shell', 'caltex', 'esso', 'susco']) {
    if (b.includes(k)) return k;
  }
  if (/^pt\b/.test(b)) return 'pt';
  return 'other';
}

/**
 * Short mark for the badge. Explicit per brand rather than "first two letters",
 * which rendered PTT as "PT" - the name of a different chain entirely.
 */
const MARKS: Record<string, string> = {
  ptt: 'PTT',
  bangchak: 'BCP',
  shell: 'SH',
  caltex: 'CX',
  esso: 'ES',
  susco: 'SUS',
  pt: 'PT',
};

function initials(brand: string): string {
  const known = MARKS[brandSlug(brand)];
  if (known) return known;
  const words = brand.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || 'F';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
